import { useEffect, useRef, useState } from 'react';
import { VERBS } from '../englishWords';
import { addBalance } from '../balance';
import { playCue } from '../cuelume';
import { ResultSticker } from '../components/GameHeader';

const img = (name) => `${process.env.PUBLIC_URL}/img/${name}`;

// No timer here: the game runs until level 5 is cleared
const LEVELS = [
  { id: 1, triples: 2 },
  { id: 2, triples: 3 },
  { id: 3, triples: 4 },
  { id: 4, triples: 5 },
  { id: 5, triples: 6 },
];

const TOTAL_TRIPLES = LEVELS.reduce((sum, item) => sum + item.triples, 0);
const MISMATCH_DELAY = 900;
const NEXT_LEVEL_DELAY = 700;

// One tint per verb so all three tenses share a color on the board
const TRIPLE_COLORS = [
  '#ffe3a9',
  '#bfe3ff',
  '#c8f2c6',
  '#ffcfe1',
  '#ded4ff',
  '#ffd8c4',
  '#c2f0e4',
  '#f0e6b2',
];

function shuffle(array) {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

const DIFFICULTY_BANDS = [
  [1, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [4, 5],
];

function pickVerbs(levelIndex, triples, usedIds) {
  const used = new Set(usedIds);
  const [minD, maxD] = DIFFICULTY_BANDS[levelIndex] || DIFFICULTY_BANDS[DIFFICULTY_BANDS.length - 1];
  const inBand = VERBS.filter(
    (verb) => verb.difficulty >= minD && verb.difficulty <= maxD && !used.has(verb.id)
  );
  const fallback = VERBS.filter((verb) => !used.has(verb.id));
  const pool = inBand.length >= triples ? inBand : fallback;
  return shuffle(pool).slice(0, triples);
}

// Every triple is one verb in three tenses; cards match by verbId.
// Later levels slide the pool toward rarer irregulars.
function buildDeck(levelIndex, triples, usedIds = []) {
  const verbs = pickVerbs(levelIndex, triples, usedIds);
  const colors = shuffle(TRIPLE_COLORS);
  const cards = [];
  verbs.forEach((verb, i) => {
    verb.forms.forEach((form, f) => {
      cards.push({
        key: `${verb.id}-${f}`,
        verbId: verb.id,
        en: form.en,
        ru: form.ru,
        color: colors[i % colors.length],
      });
    });
  });
  return shuffle(cards);
}

// Browser TTS: free, offline-capable, no extra dependencies
function speakWord(text) {
  const synth = window.speechSynthesis;
  if (!synth) return;
  try {
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    const voice = synth
      .getVoices()
      .find((v) => v.lang === 'en-US') || synth.getVoices().find((v) => v.lang.startsWith('en'));
    if (voice) utterance.voice = voice;
    synth.speak(utterance);
  } catch {
    // TTS is a nice-to-have; never break the game because of it
  }
}

function computeScore(attempts) {
  const base = TOTAL_TRIPLES * 200;
  const missPenalty = Math.max(0, attempts - TOTAL_TRIPLES) * 25;
  return Math.max(0, base - missPenalty) * 3;
}

function EnglishGame({ onExit }) {
  const usedVerbIds = useRef([]);
  const [levelIndex, setLevelIndex] = useState(0);
  const [round, setRound] = useState(0);
  const [deck, setDeck] = useState(() => {
    const first = buildDeck(0, LEVELS[0].triples);
    usedVerbIds.current = [...new Set(first.map((card) => card.verbId))];
    return first;
  });
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [missed, setMissed] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [collected, setCollected] = useState(0);
  const [finished, setFinished] = useState(false);
  const mismatchTimer = useRef(null);
  const advanceTimer = useRef(null);
  const resultSettled = useRef(false);

  useEffect(
    () => () => {
      clearTimeout(mismatchTimer.current);
      clearTimeout(advanceTimer.current);
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    },
    []
  );

  useEffect(() => {
    if (!finished || resultSettled.current) return;
    resultSettled.current = true;
    addBalance(computeScore(attempts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const openLevel = (index) => {
    if (index === 0) usedVerbIds.current = [];
    const nextDeck = buildDeck(index, LEVELS[index].triples, usedVerbIds.current);
    usedVerbIds.current = [
      ...usedVerbIds.current,
      ...new Set(nextDeck.map((card) => card.verbId)),
    ];
    setLevelIndex(index);
    setDeck(nextDeck);
    setRound((r) => r + 1);
    setFlipped([]);
    setMatched([]);
    setMissed([]);
  };

  const restart = () => {
    clearTimeout(mismatchTimer.current);
    clearTimeout(advanceTimer.current);
    openLevel(0);
    setAttempts(0);
    setCollected(0);
    setFinished(false);
    resultSettled.current = false;
  };

  const handleCardClick = (index) => {
    if (finished || matched.includes(index)) return;
    if (flipped.includes(index) || flipped.length === 3 || missed.length) return;

    speakWord(deck[index].en);

    const nextFlipped = [...flipped, index];
    setFlipped(nextFlipped);
    playCue('tick');

    if (nextFlipped.length < 3) return;

    setAttempts((a) => a + 1);
    const [first, second, third] = nextFlipped;
    const sameVerb =
      deck[first].verbId === deck[second].verbId && deck[second].verbId === deck[third].verbId;

    if (sameVerb) {
      const nextMatched = [...matched, ...nextFlipped];
      setMatched(nextMatched);
      setFlipped([]);
      setCollected((c) => c + 1);

      if (nextMatched.length === deck.length) {
        if (levelIndex === LEVELS.length - 1) {
          playCue('success');
          setFinished(true);
        } else {
          playCue('bloom');
          advanceTimer.current = setTimeout(() => {
            openLevel(levelIndex + 1);
          }, NEXT_LEVEL_DELAY);
        }
      } else {
        playCue('sparkle');
      }
    } else {
      setMissed(nextFlipped);
      playCue('error');
      mismatchTimer.current = setTimeout(() => {
        setMissed([]);
        setFlipped([]);
      }, MISMATCH_DELAY);
    }
  };

  return (
    <div className="app">
      <header className="screen-header">
        <button
          type="button"
          className="icon-btn"
          data-cuelume-press
          data-cuelume-release="droplet"
          onClick={onExit}
          aria-label="Назад"
        >
          <img src={img('icon-arrow-left.svg')} alt="" draggable="false" />
        </button>
        <h1 className="screen-title">English: 3 времени</h1>
        <span className="icon-btn static" aria-hidden="true">
          <img src={img('icon-flash.svg')} alt="" draggable="false" />
        </span>
      </header>

      <div className="score-panel" aria-label={`Уровень ${levelIndex + 1} из ${LEVELS.length}`}>
        <div className="score-cell">
          <span className="score-value">
            {finished ? LEVELS.length : levelIndex + 1}/{LEVELS.length}
          </span>
          <span className="score-caption">Уровень</span>
        </div>
        <span className="score-divider" aria-hidden="true" />
        <div className="score-cell">
          <span className="score-value">{attempts}</span>
          <span className="score-caption">Попыток</span>
        </div>
        <span className="score-divider" aria-hidden="true" />
        <div className="score-cell">
          <span className="score-value">
            {collected}/{TOTAL_TRIPLES}
          </span>
          <span className="score-caption">Троек</span>
        </div>
      </div>

      <main
        className="board eng-board"
        key={`eng-${levelIndex}-${round}`}
        style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
      >
        {deck.map((card, index) => {
          const isRevealed = flipped.includes(index) || matched.includes(index);
          const isMatched = matched.includes(index);
          const isMissed = missed.includes(index);

          return (
            <button
              key={card.key}
              type="button"
              className={[
                'card',
                'eng-card',
                isRevealed ? 'is-flipped' : '',
                isMatched ? 'is-matched' : '',
                isMissed ? 'is-missed' : '',
              ].join(' ')}
              style={{ '--i': index, '--word-color': card.color }}
              onClick={() => handleCardClick(index)}
              disabled={isMatched}
              aria-label={isRevealed ? `${card.en} — ${card.ru}` : 'Закрытая карточка'}
            >
              <span className="card-shake">
                <span className="card-inner">
                  <span className="card-face card-front">
                    <img className="card-mark" src={img('card-back.png')} alt="" draggable="false" />
                  </span>
                  <span className="card-face card-back eng-word">
                    <span className="eng-word-en">{card.en}</span>
                    <span className="eng-word-ru">{card.ru}</span>
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </main>

      {finished && (
        <div className="result" role="dialog" aria-label="Результат игры">
          <div className="result-card">
            <ResultSticker won winVideo="sticker-win-pair.mp4" />
            <p className="result-title">Все 5 уровней пройдены!</p>
            <p className="result-score">{computeScore(attempts)}</p>
            <p className="result-score-label">очков</p>
            <div className="result-stats">
              <div className="stat">
                <span className="stat-label">Уровень</span>
                <span className="stat-value">
                  {LEVELS.length}/{LEVELS.length}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Попытки</span>
                <span className="stat-value">{attempts}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Тройки</span>
                <span className="stat-value">
                  {collected}/{TOTAL_TRIPLES}
                </span>
              </div>
            </div>
            <div className="result-actions">
              <button
                type="button"
                className="restart"
                data-cuelume-press="pulse"
                data-cuelume-release="bloom"
                onClick={restart}
              >
                Играть снова
              </button>
              <button
                type="button"
                className="home-btn"
                data-cuelume-press
                data-cuelume-release="droplet"
                onClick={onExit}
              >
                На главную
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnglishGame;
